import {Badge} from '@dropbox/dig-components/badge';
import {IconButton} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CopyLine, RepeatLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {AdminMigrationsService, type AlembicRevisionSummary} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {type MouseEvent, type ReactNode, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';

const revisionPathColumn: Column<AlembicRevisionSummary> = {
  type: 'Path',
  minWidth: 260,
  sort: (a, b) => (a.path ?? '').localeCompare(b.path ?? ''),
};

const timestampColumn: Column<AlembicRevisionSummary> = {
  type: 'Timestamp',
  fixed: true,
  minWidth: 160,
  sort: (a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''),
};

const impactedTablesColumn: Column<AlembicRevisionSummary> = {
  type: 'Impacted tables',
  minWidth: 220,
  width: 240,
  sort: (a, b) =>
    (a.impacted_tables ?? []).join(',').localeCompare((b.impacted_tables ?? []).join(',')),
};

const baseRevisionColumns: Column<AlembicRevisionSummary>[] = [
  {
    type: 'Revision',
    fixed: true,
    minWidth: 130,
    sort: (a, b) => a.revision.localeCompare(b.revision),
  },
  {
    type: 'Down revision',
    fixed: true,
    minWidth: 130,
    sort: (a, b) => a.down_revisions.join(',').localeCompare(b.down_revisions.join(',')),
  },
  timestampColumn,
  {
    type: 'Message',
    minWidth: 300,
    width: 320,
    sort: (a, b) => (a.message ?? '').localeCompare(b.message ?? ''),
  },
  impactedTablesColumn,
  {type: 'Flags', fixed: true, minWidth: 110},
  revisionPathColumn,
];

const recentRevisionColumns: Column<AlembicRevisionSummary>[] = [
  ...baseRevisionColumns.slice(0, 6),
  {
    type: 'Author',
    fixed: true,
    minWidth: 160,
    sort: (a, b) => (a.author?.name ?? '').localeCompare(b.author?.name ?? ''),
  },
  revisionPathColumn,
];

const formatRevisionList = (revisions: string[]) => (revisions.length ? revisions.join(', ') : '-');

const formatTimestamp = (timestamp?: string | null) =>
  timestamp ? timestamp.replace('T', ' ').slice(0, 16) : '-';

const deploymentStatusLabels: Record<string, string> = {
  local_only: 'Local only',
  main_not_prod: 'Not prod',
};

const deploymentStatusBackgrounds: Record<string, string> = {
  local_only: 'rgba(245, 131, 31, 0.12)',
  main_not_prod: 'rgba(246, 188, 0, 0.16)',
};

const getRevisionCellStyle = (deploymentStatus?: AlembicRevisionSummary['deployment_status']) => {
  if (!deploymentStatus) {
    return undefined;
  }
  const background = deploymentStatusBackgrounds[deploymentStatus];
  return background ? {backgroundColor: background} : undefined;
};

const PATH_SUFFIX_LENGTH = 24;

const splitPathForMiddleTruncation = (value: string) => {
  if (value.length <= PATH_SUFFIX_LENGTH) {
    return {prefix: '', suffix: value};
  }

  return {
    prefix: value.slice(0, -PATH_SUFFIX_LENGTH),
    suffix: value.slice(-PATH_SUFFIX_LENGTH),
  };
};

const statusLabel = ({
  error,
  is_at_head,
  has_multiple_heads,
  current_revisions,
}: {
  error?: string | null;
  is_at_head?: boolean;
  has_multiple_heads?: boolean;
  current_revisions?: string[];
}) => {
  if (error) {
    return 'Unavailable';
  }
  if (has_multiple_heads) {
    return 'Multiple heads';
  }
  if (!current_revisions?.length) {
    return 'No DB revision';
  }
  return is_at_head ? 'At head' : 'Behind';
};

const SummaryCell = ({label, children}: {label: string; children: ReactNode}) => (
  <Box
    padding="12"
    style={{
      border: '1px solid var(--dig-color__border__subtle)',
      minWidth: 180,
      flex: '1 1 180px',
    }}
  >
    <Stack gap="4">
      <Text size="small" color="subtle">
        {label}
      </Text>
      <Box>{children}</Box>
    </Stack>
  </Box>
);

const MonospaceText = ({children, isBold}: {children: ReactNode; isBold?: boolean}) => (
  <Text isBold={isBold} style={{fontFamily: 'monospace', overflowWrap: 'anywhere'}}>
    {children}
  </Text>
);

const PathCell = ({path}: {path?: string | null}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!path) {
    return <Text color="faint">-</Text>;
  }

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await navigator.clipboard.writeText(path);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  const {prefix, suffix} = splitPathForMiddleTruncation(path);

  return (
    <Box
      data-testid="migration-path-cell"
      className={atoms({display: 'flex', alignItems: 'center'})}
      style={{gap: 8, minWidth: 0}}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Tooltip title={path}>
        <Box style={{minWidth: 0, flex: '1 1 auto', maxWidth: '100%'}}>
          <Text
            data-testid="migration-path-text"
            style={{
              display: 'flex',
              fontFamily: 'monospace',
              minWidth: 0,
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {prefix ? (
              <span
                data-testid="migration-path-prefix"
                style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}
              >
                {prefix}
              </span>
            ) : null}
            <span data-testid="migration-path-suffix" style={{flex: '0 0 auto'}}>
              {suffix}
            </span>
          </Text>
        </Box>
      </Tooltip>
      <Box
        className={atoms({display: 'flex', alignItems: 'center', justifyContent: 'center'})}
        style={{width: 24, height: 24, flex: '0 0 24px', lineHeight: 0}}
      >
        <Tooltip title={copied ? 'Copied' : 'Copy path'}>
          <IconButton
            aria-hidden={!isHovered}
            aria-label="Copy migration path"
            size="small"
            tabIndex={isHovered ? 0 : -1}
            variant="borderless"
            style={{
              height: 24,
              minWidth: 24,
              opacity: isHovered ? 1 : 0,
              pointerEvents: isHovered ? 'auto' : 'none',
              visibility: isHovered ? 'visible' : 'hidden',
              width: 24,
            }}
            onClick={(event: MouseEvent<HTMLButtonElement>) => void handleCopy(event)}
          >
            <UIIcon src={CopyLine} size="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const AuthorCell = ({author}: {author?: AlembicRevisionSummary['author']}) => {
  if (!author) {
    return <Text color="faint">-</Text>;
  }

  const avatarUser = {name: author.name, ldap: author.ldap ?? undefined};

  const content = (
    <Box className={atoms({display: 'flex', alignItems: 'center'})} style={{gap: 6, minWidth: 0}}>
      <Avatar user={avatarUser} size="xsmall" />
      <Text
        style={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {author.name}
      </Text>
    </Box>
  );

  if (!author.ldap) {
    return content;
  }

  return (
    <PersonHoverCard ldap={author.ldap} name={author.name}>
      <Link
        to={ROUTE_PATHS.PROFILE.replace(':userid', author.ldap)}
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
        style={{display: 'inline-flex', maxWidth: '100%'}}
      >
        {content}
      </Link>
    </PersonHoverCard>
  );
};

const ImpactedTablesCell = ({tables}: {tables?: string[] | null}) => {
  if (!tables?.length) {
    return <Text color="faint">-</Text>;
  }

  const tableList = tables.join(', ');

  return (
    <Tooltip title={tableList}>
      <Text
        style={{
          display: 'block',
          fontFamily: 'monospace',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {tableList}
      </Text>
    </Tooltip>
  );
};

const RevisionFlags = ({revision}: {revision: AlembicRevisionSummary}) => {
  const flags: string[] = [];
  if (revision.is_current) flags.push('Current');
  if (revision.is_head) flags.push('Head');
  if (revision.is_pending) flags.push('Pending');
  if (revision.deployment_status) {
    flags.push(deploymentStatusLabels[revision.deployment_status] ?? revision.deployment_status);
  }

  if (!flags.length) {
    return <Text color="faint">-</Text>;
  }

  return (
    <Box className={atoms({display: 'flex', flexWrap: 'wrap'})} style={{gap: 4}}>
      {flags.map((flag) => (
        <Badge key={flag} size="xsmall">
          {flag}
        </Badge>
      ))}
    </Box>
  );
};

const RevisionRow = ({
  revision,
  showAuthor,
}: {
  revision: AlembicRevisionSummary;
  showAuthor?: boolean;
}) => {
  const cellStyle = getRevisionCellStyle(revision.deployment_status);

  return (
    <Table.Row data-testid={`migration-row-${revision.revision}`}>
      <Table.Cell style={cellStyle}>
        <MonospaceText>{revision.revision}</MonospaceText>
      </Table.Cell>
      <Table.Cell style={cellStyle}>
        <MonospaceText>{formatRevisionList(revision.down_revisions)}</MonospaceText>
      </Table.Cell>
      <Table.Cell style={cellStyle}>
        <MonospaceText>{formatTimestamp(revision.created_at)}</MonospaceText>
      </Table.Cell>
      <Table.Cell style={cellStyle}>
        <Text>{revision.message ?? '-'}</Text>
      </Table.Cell>
      <Table.Cell style={cellStyle}>
        <ImpactedTablesCell tables={revision.impacted_tables} />
      </Table.Cell>
      <Table.Cell style={cellStyle}>
        <RevisionFlags revision={revision} />
      </Table.Cell>
      {showAuthor ? (
        <Table.Cell style={cellStyle}>
          <AuthorCell author={revision.author} />
        </Table.Cell>
      ) : null}
      <Table.Cell style={cellStyle}>
        <PathCell path={revision.path} />
      </Table.Cell>
    </Table.Row>
  );
};

const RevisionTable = ({
  title,
  revisions,
  isLoading,
  emptyText,
  showAuthor,
}: {
  title: string;
  revisions: AlembicRevisionSummary[];
  isLoading: boolean;
  emptyText: string;
  showAuthor?: boolean;
}) => (
  <Stack gap="12">
    <Title size={18}>{title}</Title>
    <Table columns={showAuthor ? recentRevisionColumns : baseRevisionColumns} data={revisions}>
      <Table.Header />
      <Table.Body>
        {isLoading ? (
          <Table.Loading rowCount={5} />
        ) : revisions.length ? (
          revisions.map((revision) => (
            <RevisionRow key={revision.revision} revision={revision} showAuthor={showAuthor} />
          ))
        ) : (
          <Table.Row>
            <Table.Cell
              colSpan={showAuthor ? recentRevisionColumns.length : baseRevisionColumns.length}
            >
              <Box paddingY="16">
                <Text color="faint">{emptyText}</Text>
              </Box>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  </Stack>
);

export const AdminMigrations = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const canView = isSuperUser(pulseUser);

  useDocumentTitle('Database migrations');

  const {data, isError, isLoading, isFetching, refetch} = useQuery({
    queryKey: ['admin', 'migrations'],
    queryFn: () =>
      getService(AdminMigrationsService).getAdminMigrationStatusApiV1AdminMigrationsGet(),
    enabled: canView,
  });

  const sortedPendingRevisions = useMemo(
    () => data?.pending_revisions ?? [],
    [data?.pending_revisions]
  );
  const recentRevisions = useMemo(() => data?.recent_revisions ?? [], [data?.recent_revisions]);

  if (!pulseUser) {
    return null;
  }

  if (!canView) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Database migrations', to: ROUTE_PATHS.ADMIN_MIGRATIONS},
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Title
          size={24}
          withAccessoryEnd={
            <Tooltip title="Refresh">
              <IconButton
                aria-label="Refresh migrations"
                variant="opacity"
                shape="circular"
                disabled={isFetching}
                onClick={() => void refetch()}
              >
                <UIIcon src={RepeatLine} />
              </IconButton>
            </Tooltip>
          }
        >
          Database migrations
        </Title>

        <Box className={atoms({display: 'flex', flexWrap: 'wrap'})} style={{gap: 12}}>
          {isLoading ? (
            <>
              <Skeleton.Box height={76} />
              <Skeleton.Box height={76} />
              <Skeleton.Box height={76} />
            </>
          ) : (
            <>
              <SummaryCell label="Status">
                <Text isBold>{statusLabel(data ?? {})}</Text>
              </SummaryCell>
              <SummaryCell label="Current DB revision">
                <MonospaceText isBold>
                  {formatRevisionList(data?.current_revisions ?? [])}
                </MonospaceText>
              </SummaryCell>
              <SummaryCell label="Code head">
                <MonospaceText isBold>{formatRevisionList(data?.heads ?? [])}</MonospaceText>
              </SummaryCell>
              <SummaryCell label="Pending">
                <Text isBold>{data?.pending_count ?? '-'}</Text>
              </SummaryCell>
            </>
          )}
        </Box>

        {(isError || data?.error) && (
          <Box
            padding="12"
            style={{
              border: '1px solid var(--dig-color__warning__base)',
              background: 'var(--dig-color__warning__surface)',
            }}
          >
            <Text>{data?.error ?? 'Unable to load migration status.'}</Text>
          </Box>
        )}

        <RevisionTable
          title="Pending migrations"
          revisions={sortedPendingRevisions}
          isLoading={isLoading}
          emptyText="No pending migrations"
        />

        <RevisionTable
          title="Recent migrations"
          revisions={recentRevisions}
          isLoading={isLoading}
          emptyText="No migrations found"
          showAuthor
        />
      </Stack>
    </Layout.Container>
  );
};
